import React, { useMemo } from 'react';
import set from 'lodash/set';
import get from 'lodash/fp/get';

import { getNestedValue } from '@/se/utilities/data/object';
import GraphQLCRUD from '@/se/components/GraphQLCRUD';

import UserInput from '../../inputs/user/UserInput';
import scheme, { usersPaginatedQuery, usersCountQuery, usersSubscription } from '../../../graph/users';
import UserFilterBar from './UserFilterBar';
import {
  baseColumns,
  lastLogin,
  lastLoginNoAction,
  nameColumn,
  openPositionNotification,
  sensorEmailNotification,
  sensorPhoneNotification,
  representsStaffColumn,
  staffCostAccessColumn,
} from './columns';
import UserShowView from './UserShowView';
import { userRoleLabels } from '../role/labels';
import UserMobileListItem from './UserMobileListItem';
import ClearLockdownButton from '../../pages/user/ClearLockdownButton';
import { useContext } from 'react';
import { SessionContext } from '../../../state/Session';
import UserRoles from '../../../types/UserRoles';

import { useScope } from '../../../hooks/useScope';

const Empty = { illustration: theme => theme.illustrations.users };

export const List = shouldFilter => {
  const sessionContext = useContext(SessionContext);
  const roles = sessionContext.session?.user?.metadata ?? [];
  const isSuperAdmin = roles.indexOf(UserRoles.SuperAdmin) >= 0;

  return {
    tableKey: 'Users',
    columns: [
      nameColumn,
      ...baseColumns,
      lastLoginNoAction,
      ...(shouldFilter ? [] : [openPositionNotification, sensorEmailNotification, sensorPhoneNotification]),
      representsStaffColumn,
      ...(isSuperAdmin ? [staffCostAccessColumn] : []),
    ],
    MobileItemComponent: UserMobileListItem,
    Empty,
    simpleFilter: true,
    // Server-side pagination configuration
    paginationConfig: {
      query: usersPaginatedQuery,
      countQuery: usersCountQuery,
      subscription: usersSubscription,
      entityKey: 'usersPaginated',
      countKey: 'usersCount',
      // pageSize defaults to 50 via DEFAULT_PAGE_SIZE
    },
    defaultSort: { title: 'Name', type: 'ASC' },
    FilterComponent: UserFilterBar,
  };
};

export const Create = {
  actionTitleButton: 'Create & Send Invitation',
  Input: set(UserInput(userRoleLabels, true), 'defaultValue', { patientNameFormat: 'FullName' }),
  Empty,
};

export const Show = shouldFilter => {
  const sessionContext = useContext(SessionContext);
  const roles = sessionContext.session?.user?.metadata ?? [];
  const isSuperAdmin = roles.indexOf(UserRoles.SuperAdmin) >= 0;

  return {
    columns: [
      ...baseColumns,
      lastLogin,
      ...(shouldFilter ? [] : [openPositionNotification, sensorEmailNotification, sensorPhoneNotification]),
      representsStaffColumn,
      ...(isSuperAdmin ? [staffCostAccessColumn] : []),
    ],
    View: UserShowView,
    Empty,
    Actions: props => <ClearLockdownButton {...props} refetch={scheme.item} />,
  };
};

export const Edit = {
  Input: UserInput(userRoleLabels, false),
  mapEditItemProps: ({ data }) => ({
    data: data && {
      ...data,
      user: {
        ...data.user,
        role: getNestedValue('user.role', data),
      },
    },
  }),
  Empty,
};

export default props => {
  const scope = useScope();
  const hospitalType = get('hospital.type', scope);

  const UsersCRUD = useMemo(
    () =>
      GraphQLCRUD({
        entityName: 'User',
        scheme,
        List: List(hospitalType === 'SurgeonOffice'),
        Create,
        Show: Show(hospitalType === 'SurgeonOffice'),
        Edit,
      }),
    [hospitalType]
  );

  return <UsersCRUD {...props} />;
};
